import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PageContent } from '@/components/PageContent';
import { Tabs, TabList, Tab, TabPanels, TabPanel, TabIndicator, useToast, Flex, Text, Switch } from '@chakra-ui/react';
import {
  useGetChallengeByUUID,
  usePutChallengeMutation,
  usePutChallengePublishedMutation,
  usePutChallengeUnpublishedMutation,
  usePutChallengeCloseMutation,
} from '@/services/queries/challenge';
import { breadcrumb } from './breadcrumb';
import { modalFormSchema, type ModalFormData } from './schemas';
import { Step1Fields } from './components/Step1Fields';
import { Step2Fields } from './components/Step2Fields';
import { Step3Fields } from './components/Step3Fields';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { Challenge, ChallengeInteractivity, ChallengeStatus } from '@/model';
import { ConfirmationModal } from '@/components/ConfirmationModal';

export const ApplicationChallengeEdit = () => {
  const [selectedChallenge, setSelectedChallenge] = useState<Challenge | undefined>(undefined);
  const [tabIndex, setTabIndex] = useState(0);
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const toast = useToast();
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);

  const {
    data: challengeData,
    isLoading: isChallengeLoading,
    error: challengeError,
    refetch: refetchChallenge,
  } = useGetChallengeByUUID(id as string, !!id);

  const putMutation = usePutChallengeMutation();
  const publishMutation = usePutChallengePublishedMutation();
  const unpublishMutation = usePutChallengeUnpublishedMutation();
  const closeMutation = usePutChallengeCloseMutation();

  const form = useForm<ModalFormData>({
    defaultValues: {
      step: 1,
      status: undefined,
      title: '',
      description: '',
      time_limit_seconds: 0,
      has_start_date: false,
      starts_in: '',
      timeout_message: '',
      error_message: '',
      winner_message: '',
      communication_message: '',
      team_uuid: '',
      questions: [],
    },
    resolver: zodResolver(modalFormSchema),
  });

  const challengeStatus = form.watch('status');

  useEffect(() => {
    if (challengeData) {
      setSelectedChallenge(challengeData);

      form.setValue('team_uuid', challengeData.team_uuid);
      form.setValue('status', challengeData.status);
      form.setValue('title', challengeData.title);
      form.setValue('description', challengeData.description);
      form.setValue('time_limit_seconds', challengeData.time_limit_seconds);
      form.setValue('has_start_date', !!challengeData.starts_in);
      form.setValue('starts_in', challengeData.starts_in);
      form.setValue('timeout_message', challengeData.timeout_message);
      form.setValue('error_message', challengeData.error_message);
      form.setValue('winner_message', challengeData.winner_message);
      form.setValue('communication_message', challengeData.communication_message);
    }
  }, [challengeData, form]);

  useEffect(() => {
    if (challengeError) {
      toast({
        status: 'error',
        title: (challengeError as any).message || t('pages.challenges_details.toasts.get_by_uuid_error'),
      });
    }
  }, [challengeError, t, toast]);

  const handleCloseChallege = () => {
    setCloseModalOpen(false);
    closeMutation.mutate(id as string, {
      onSuccess: () => {
        toast({
          status: 'success',
          title: t('pages.challenges_details.toasts.close_success'),
        });
        refetchChallenge();
      },
      onError: (response: any) => {
        toast({
          status: 'error',
          title: (response && response.message) || t('pages.challenges_details.toasts.close_error'),
        });
      },
    });
  };

  const handleFinalSubmit = (status: ChallengeStatus) => form.handleSubmit(data => onSubmit(data, status))();

  const onSubmit = (data: ModalFormData, status: ChallengeStatus | undefined) => {
    if (!status) return;

    const images: Record<string, File> = {};
    data.questions?.forEach(option => {
      if (option.image) {
        images[option.uuid] = option.image;
      }
    });

    const payload = {
      ...(data.team_uuid ? { team_uuid: data.team_uuid } : {}),
      title: data.title,
      description: data.description,
      time_limit_seconds: data.time_limit_seconds,
      status,
      starts_in: data.starts_in,
      error_message: data.error_message,
      timeout_message: data.timeout_message,
      communication_message: data.communication_message,
      winner_message: data.winner_message,
      questions: data.questions.map(question => ({
        uuid: question.uuid,
        title: question.title,
        answer: question.answer.map(answer => ({
          uuid: answer.uuid,
          content: answer.content,
          is_correct: answer.is_correct,
        })),
      })),
      ...images,
    };

    putMutation.mutate(
      { id, payload },
      {
        onSuccess: () => {
          toast({
            status: 'success',
            title: t('pages.challenges_details.toasts.edit_success'),
          });
          form.setValue('status', status);
          navigate('/application/challenge');
        },
        onError: (resp: any) => {
          form.setValue('status', challengeStatus);
          toast({
            status: 'error',
            title: (resp && resp.message) || t('pages.challenges_details.toasts.edit_error'),
          });
        },
      },
    );
  };

  return (
    <PageContent
      title={
        <Flex alignItems='center' gap={4}>
          <Text>{t('pages.challenges_details.edit_title')}</Text>

          <Text fontSize='14px' fontWeight='normal'>
            {t('pages.challenges_details.switch_published')}
          </Text>
          <Switch
            name='status'
            size='sm'
            isChecked={challengeStatus === ChallengeStatus.PUBLISHED}
            disabled={
              selectedChallenge?.interactivity === ChallengeInteractivity.CLOSED &&
              challengeStatus === ChallengeStatus.NOT_PUBLISHED
            }
            onChange={event => {
              const checked = event.target.checked;
              const newStatus = checked ? ChallengeStatus.PUBLISHED : ChallengeStatus.NOT_PUBLISHED;
              const previousStatus = form.getValues('status');

              form.setValue('status', newStatus);

              const handleError = (response: any, fallbackMessageKey: string) => {
                // Reverte o valor em caso de erro
                form.setValue('status', previousStatus);

                toast({
                  status: 'error',
                  title: (response && response.message) || t(fallbackMessageKey),
                });
              };

              if (checked) {
                publishMutation.mutate(id as string, {
                  onSuccess: () => {
                    toast({
                      status: 'success',
                      title: t('pages.challenges_details.toasts.publish_success'),
                    });
                  },
                  onError: (response: any) => {
                    handleError(response, 'pages.challenges_details.toasts.publish_error');
                  },
                });
              } else {
                unpublishMutation.mutate(id as string, {
                  onSuccess: () => {
                    toast({
                      status: 'success',
                      title: t('pages.challenges_details.toasts.unpublish_success'),
                    });
                  },
                  onError: (response: any) => {
                    handleError(response, 'pages.challenges_details.toasts.unpublish_error');
                  },
                });
              }
            }}
          />
        </Flex>
      }
      breadcrumbItems={breadcrumb}
    >
      <Tabs
        variant='unstyled'
        borderTopWidth='8px'
        borderRadius='card'
        shadow='md'
        onChange={tabIndex => {
          setTabIndex(tabIndex);
          form.setValue('step', tabIndex + 1);
        }}
        index={tabIndex}
      >
        <TabList border={0}>
          <Tab>{t('pages.challenges_details.tabs.tab_1.title')}</Tab>
          <Tab>{t('pages.challenges_details.tabs.tab_2.title')}</Tab>
          <Tab>{t('pages.challenges_details.tabs.tab_3.title')}</Tab>
        </TabList>

        <TabIndicator mt='-48px' height='8px' bg='brand.500' borderTopLeftRadius={tabIndex === 0 ? '40px' : undefined} />

        <FormProvider {...form}>
          <TabPanels as='form' ref={formRef} noValidate>
            <TabPanel>
              <Step1Fields onStepSuccess={() => setTabIndex(1)} onCloseChallenge={() => setCloseModalOpen(true)} />
            </TabPanel>
            <TabPanel>
              <Step2Fields
                item={selectedChallenge}
                onStepSuccess={() => setTabIndex(2)}
                onCloseChallenge={() => setCloseModalOpen(true)}
              />
            </TabPanel>
            <TabPanel>
              <Step3Fields onStepSuccess={handleFinalSubmit} onCloseChallenge={() => setCloseModalOpen(true)} />
            </TabPanel>
          </TabPanels>
        </FormProvider>
      </Tabs>

      <ConfirmationModal
        isOpen={closeModalOpen}
        onClose={() => setCloseModalOpen(false)}
        onConfirm={handleCloseChallege}
        title='Encerrar desafio'
        description='Tem certeza que deseja encerrar esse desafio? Todas as participações em andamento serão interrompidas.'
        isLoading={closeMutation.isLoading}
      />

      <CenteredSpinner
        isLoading={putMutation.isLoading || isChallengeLoading || publishMutation.isLoading || unpublishMutation.isLoading}
      />
    </PageContent>
  );
};
